/**
 * Social Image Service - API calls for AI-powered social media image generation
 * Handles image generation, job polling, and related operations
 */

import {
    generateSocialImage,
    getSocialImageJob,
    getSocialImages,
    deleteSocialImage,
    requestDesignerService,
    // Session APIs
    createImageSession,
    listImageSessions,
    getImageSession,
    sendSessionRefinement,
    deleteImageSession
} from '../api/socialMedia';

// Re-export session APIs for convenience
export {
    createImageSession,
    listImageSessions,
    getImageSession,
    sendSessionRefinement,
    deleteImageSession
};

// Visual styles for social media images
export const IMAGE_STYLES = [
    { id: 'modern', name: 'Modern', description: 'Clean lines, gradient backgrounds', color: 'from-slate-600 to-slate-800' },
    { id: 'professional', name: 'Professional', description: 'Corporate, trust-building', color: 'from-blue-600 to-blue-800' },
    { id: 'bold', name: 'Bold', description: 'High contrast, attention-grabbing', color: 'from-red-500 to-orange-500' },
    { id: 'minimalist', name: 'Minimalist', description: 'Simple, elegant design', color: 'from-gray-400 to-gray-600' },
    { id: 'vibrant', name: 'Vibrant', description: 'Colorful, energetic', color: 'from-pink-500 to-purple-500' },
    { id: 'luxury', name: 'Luxury', description: 'Premium, sophisticated', color: 'from-amber-400 to-yellow-600' }
];

// Content formats - engagement-driven templates that get clicks
export const CONTENT_FORMATS = [
    {
        id: 'infographic',
        name: 'Infographic',
        icon: 'BarChart3',
        description: 'Visual data storytelling with icons, charts, and step-by-step flows',
        promptHint: 'Create an infographic-style graphic with numbered steps, icons for each point, visual hierarchy, and easy-to-scan sections. Use connecting lines or arrows to show flow.',
        bestFor: ['Educational content', 'How-to guides', 'Process explanations'],
        engagementTip: 'Infographics get 3x more shares than other content types'
    },
    {
        id: 'stat-highlight',
        name: 'Stat Highlight',
        icon: 'TrendingUp',
        description: 'Big numbers that stop the scroll - percentages, rates, savings',
        promptHint: 'Create a stat-focused graphic with an oversized number as the hero element, supporting context text, and a clean background that makes the statistic impossible to miss.',
        bestFor: ['Rate announcements', 'Market updates', 'Savings showcases'],
        engagementTip: 'Posts with statistics get 73% more engagement'
    },
    {
        id: 'quote-card',
        name: 'Quote Card',
        icon: 'Quote',
        description: 'Testimonials and wisdom that build trust and credibility',
        promptHint: 'Create an elegant quote card with large decorative quotation marks, the quote as the focal point, attribution at the bottom, and a sophisticated background that conveys trust.',
        bestFor: ['Client testimonials', 'Industry wisdom', 'Motivational content'],
        engagementTip: 'Testimonial posts increase conversion by 34%'
    },
    {
        id: 'listicle',
        name: 'Tips List',
        icon: 'ListChecks',
        description: 'Numbered tips or checklists that provide instant value',
        promptHint: 'Create a visually appealing list graphic with numbered items, icons or checkmarks for each point, clear hierarchy, and enough spacing for mobile readability. Make it feel like valuable, save-worthy content.',
        bestFor: ['Tips & tricks', 'Checklists', 'Do\'s and Don\'ts'],
        engagementTip: 'List posts get 2x more saves than other formats'
    },
    {
        id: 'comparison',
        name: 'Comparison',
        icon: 'ArrowLeftRight',
        description: 'Side-by-side comparisons, before/after, myths vs facts',
        promptHint: 'Create a split-design comparison graphic with two distinct sides, clear labels for each side, contrasting colors or treatments, and visual elements that highlight the differences.',
        bestFor: ['Myth busting', 'Before/After', 'Option comparisons'],
        engagementTip: 'Comparison posts spark 40% more comments'
    },
    {
        id: 'carousel-cover',
        name: 'Carousel Cover',
        icon: 'Layers',
        description: 'Eye-catching first slide that makes people swipe',
        promptHint: 'Create a compelling carousel cover slide with a hook headline that creates curiosity, "swipe to learn more" visual cue, bold typography, and a design that demands the viewer swipe right.',
        bestFor: ['Educational series', 'Step-by-step guides', 'Story content'],
        engagementTip: 'Carousels get 1.4x more reach than single images'
    },
    {
        id: 'announcement',
        name: 'Announcement',
        icon: 'Megaphone',
        description: 'News, launches, and updates that demand attention',
        promptHint: 'Create an attention-grabbing announcement graphic with bold "NEW" or "ANNOUNCEMENT" badge, clear headline, celebratory or urgent design elements, and a strong call-to-action.',
        bestFor: ['New services', 'Rate changes', 'Company news'],
        engagementTip: 'Announcement posts get 2.5x more clicks'
    },
    {
        id: 'question',
        name: 'Question Hook',
        icon: 'HelpCircle',
        description: 'Thought-provoking questions that drive comments',
        promptHint: 'Create an engaging question graphic with the question as the hero text, a thought-provoking or curious visual treatment, and design that invites the viewer to pause and think. Include a subtle "Comment below" prompt.',
        bestFor: ['Engagement posts', 'Polls', 'Community building'],
        engagementTip: 'Question posts get 100% more comments'
    },
    {
        id: 'meme-style',
        name: 'Relatable Meme',
        icon: 'Smile',
        description: 'Humorous, relatable content that gets shared',
        promptHint: 'Create a clean, professional-yet-fun meme-style graphic with relatable humor about the home buying or mortgage process. Keep it light, shareable, and on-brand without being unprofessional.',
        bestFor: ['Humor', 'Relatable moments', 'Viral content'],
        engagementTip: 'Humor posts get 5x more shares'
    },
    {
        id: 'cta-banner',
        name: 'CTA Banner',
        icon: 'MousePointerClick',
        description: 'Direct response ads optimized for clicks',
        promptHint: 'Create a conversion-focused banner with a compelling headline, clear value proposition, prominent call-to-action button, and urgency elements. Design for paid social ads.',
        bestFor: ['Paid ads', 'Lead generation', 'Promotions'],
        engagementTip: 'CTA-focused posts get 285% more clicks'
    }
];

// Aspect ratios for different platforms
export const ASPECT_RATIOS = [
    { id: '1:1', name: 'Square (1:1)', description: 'Instagram feed, Facebook', width: 1080, height: 1080 },
    { id: '16:9', name: 'Landscape (16:9)', description: 'LinkedIn, YouTube, Facebook cover', width: 1920, height: 1080 },
    { id: '9:16', name: 'Portrait (9:16)', description: 'Instagram Stories, TikTok, Reels', width: 1080, height: 1920 }
];

// Job status constants
export const JOB_STATUS = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed'
};

// Credit costs for AI generation
export const CREDIT_COSTS = {
    social_image: 50
};

// Professional designer service pricing (150%+ margin on $6 cost)
export const DESIGNER_PACKAGES = [
    { id: 'single', images: 1, price: 19.99, perImage: 19.99 },
    { id: 'starter', images: 3, price: 49.99, perImage: 16.66 },
    { id: 'standard', images: 5, price: 79.99, perImage: 16.00, popular: true },
    { id: 'pro', images: 10, price: 149.99, perImage: 15.00 },
];

// Polling configuration
export const POLL_INTERVAL = 2000;  // 2 seconds
export const POLL_TIMEOUT = 120000; // 2 minutes

// Example prompts for design instructions
export const EXAMPLE_PROMPTS = [
    'Create a professional mortgage tips graphic with icons and bullet points',
    'Design an eye-catching rate announcement with large numbers',
    'Make a testimonial post with a quote bubble and subtle home imagery',
    'Create a "Just Sold" celebration graphic with confetti elements',
    'Design an educational infographic about home buying steps'
];

// Quick-start presets for common lender marketing use cases
export const PLATFORM_PRESETS = [
    // High Engagement Templates
    {
        id: 'rate-update',
        name: 'Rate Update',
        category: 'engagement',
        format: 'stat-highlight',
        style: 'bold',
        aspectRatio: '1:1',
        caption: 'Today\'s mortgage rate update',
        instructions: 'Create a bold market update graphic with oversized rate numbers, a clear headline, subtle chart-inspired accents, and a strong call to action to contact me for personalized quotes.',
        description: 'High-visibility stat graphic for daily or weekly rate changes',
        emoji: '📊'
    },
    {
        id: 'homebuyer-tips-infographic',
        name: '5 Tips Infographic',
        category: 'engagement',
        format: 'infographic',
        style: 'professional',
        aspectRatio: '1:1',
        caption: '5 Tips for First-Time Homebuyers',
        instructions: 'Create an infographic with 5 numbered tips, each with a small icon. Use a clean vertical flow, professional blue color scheme, and make each tip scannable in 2-3 seconds. Include a "Save this post!" prompt.',
        description: 'Shareable infographic that gets saved',
        emoji: '📋'
    },
    {
        id: 'myth-vs-fact',
        name: 'Myth vs Fact',
        category: 'engagement',
        format: 'comparison',
        style: 'bold',
        aspectRatio: '1:1',
        caption: 'Mortgage Myth vs Reality',
        instructions: 'Create a split comparison graphic with "MYTH" on one side (red/dark) and "FACT" on the other (green/bright). Use X and checkmark icons, bold typography, and make it feel like myth-busting content.',
        description: 'Myth-busting content that sparks discussion',
        emoji: '🔍'
    },
    {
        id: 'client-testimonial',
        name: 'Client Win',
        category: 'trust',
        format: 'quote-card',
        style: 'luxury',
        aspectRatio: '1:1',
        caption: 'What my clients are saying',
        instructions: 'Create a premium testimonial graphic with elegant quote treatment, large decorative quotation marks, warm neutral tones, 5-star rating visual, and the client\'s first name with "Verified Buyer" badge.',
        description: 'Social proof that builds trust',
        emoji: '⭐'
    },
    {
        id: 'carousel-hook',
        name: 'Carousel: Swipe to Learn',
        category: 'engagement',
        format: 'carousel-cover',
        style: 'vibrant',
        aspectRatio: '1:1',
        caption: 'Swipe to see the home buying process',
        instructions: 'Create an irresistible carousel cover with a hook headline like "The 7 Steps to Your Dream Home" or similar. Include a prominent swipe arrow, bold colors, and curiosity-inducing design that demands engagement.',
        description: 'First slide that gets people swiping',
        emoji: '👆'
    },
    {
        id: 'question-engagement',
        name: 'Question Post',
        category: 'engagement',
        format: 'question',
        style: 'modern',
        aspectRatio: '1:1',
        caption: 'What\'s stopping you from buying your first home?',
        instructions: 'Create an engaging question graphic with the question as large, bold text. Add thought bubble or question mark design elements. Include "Comment below!" prompt. Make it feel conversational and inviting.',
        description: 'Comment-driving engagement post',
        emoji: '💬'
    },
    {
        id: 'savings-stat',
        name: 'Savings Highlight',
        category: 'conversion',
        format: 'stat-highlight',
        style: 'bold',
        aspectRatio: '1:1',
        caption: 'My clients saved $XX,XXX on average',
        instructions: 'Create a stat-focused graphic with an oversized dollar amount or percentage as the hero. Include "Average client savings" context, subtle money/savings imagery, and a "Get your free quote" call-to-action.',
        description: 'Show your value with numbers',
        emoji: '💰'
    },
    {
        id: 'checklist',
        name: 'Home Buying Checklist',
        category: 'engagement',
        format: 'listicle',
        style: 'professional',
        aspectRatio: '9:16',
        caption: 'Your Home Buying Checklist',
        instructions: 'Create a vertical checklist graphic with checkbox icons, 6-8 actionable items, clean typography, and "Screenshot & Save" prompt at the bottom. Make it feel like a valuable resource worth saving.',
        description: 'Save-worthy checklist for Stories',
        emoji: '✅'
    },
    {
        id: 'announcement-new',
        name: 'Big Announcement',
        category: 'awareness',
        format: 'announcement',
        style: 'vibrant',
        aspectRatio: '1:1',
        caption: 'Exciting news!',
        instructions: 'Create an attention-grabbing announcement graphic with "NEW" or "BIG NEWS" badge, confetti or celebration elements, bold headline space for the announcement, and energy that demands attention.',
        description: 'Make noise with big news',
        emoji: '🎉'
    },
    {
        id: 'just-sold',
        name: 'Just Closed!',
        category: 'trust',
        format: 'announcement',
        style: 'modern',
        aspectRatio: '1:1',
        caption: 'Just closed!',
        instructions: 'Create a celebratory "JUST CLOSED" or "SOLD" announcement with a modern badge/ribbon, key emoji, confetti accents, space for city/neighborhood name, and a feel-good, success-oriented design.',
        description: 'Celebrate wins and build credibility',
        emoji: '🔑'
    },
    {
        id: 'linkedin-insight',
        name: 'LinkedIn Thought Leadership',
        category: 'awareness',
        format: 'quote-card',
        style: 'minimalist',
        aspectRatio: '16:9',
        caption: 'Mortgage market insight',
        instructions: 'Design a sharp, editorial-style graphic with restrained colors, confident typography, business-ready composition, and a layout that feels native to LinkedIn. Include a thought-provoking headline or insight.',
        description: 'Professional content for LinkedIn',
        emoji: '💼'
    },
    {
        id: 'story-poll',
        name: 'Story Poll',
        category: 'engagement',
        format: 'question',
        style: 'vibrant',
        aspectRatio: '9:16',
        caption: 'Quick poll!',
        instructions: 'Create a Stories-optimized poll graphic with a fun question, two clear options (A vs B style), playful design, and space for the Instagram poll sticker. Make it feel interactive and tappable.',
        description: 'Drive Story engagement with polls',
        emoji: '📊'
    },
    {
        id: 'market-update',
        name: 'Market Update',
        category: 'awareness',
        format: 'infographic',
        style: 'professional',
        aspectRatio: '1:1',
        caption: 'This week\'s market update',
        instructions: 'Create a market update infographic with 3-4 key metrics (rates, inventory, median price, etc.), up/down arrows for trends, clean data visualization style, and "Follow for weekly updates" prompt.',
        description: 'Weekly market data visualization',
        emoji: '📈'
    },
    {
        id: 'cta-consultation',
        name: 'Free Consultation CTA',
        category: 'conversion',
        format: 'cta-banner',
        style: 'bold',
        aspectRatio: '1:1',
        caption: 'Get your free consultation',
        instructions: 'Create a conversion-focused graphic with "FREE CONSULTATION" as the headline, bullet points of what they\'ll get, a prominent "Book Now" or "DM Me" button design, and urgency elements like "Limited spots".',
        description: 'Lead generation focused post',
        emoji: '📞'
    },
    {
        id: 'relatable-meme',
        name: 'Relatable Mortgage Humor',
        category: 'engagement',
        format: 'meme-style',
        style: 'modern',
        aspectRatio: '1:1',
        caption: 'When you finally get pre-approved',
        instructions: 'Create a clean, professional-but-fun meme-style graphic about a relatable home buying moment. Use modern design (not cheap meme style), relatable scenario, and humor that mortgage professionals would appreciate.',
        description: 'Shareable humor that humanizes your brand',
        emoji: '😂'
    },
    {
        id: 'do-dont',
        name: 'Do\'s and Don\'ts',
        category: 'engagement',
        format: 'comparison',
        style: 'professional',
        aspectRatio: '1:1',
        caption: 'Before applying for a mortgage',
        instructions: 'Create a split Do\'s and Don\'ts graphic with green checkmarks for Do\'s and red X\'s for Don\'ts. Include 3-4 items on each side, clean layout, and make it feel like essential knowledge worth saving.',
        description: 'Educational comparison content',
        emoji: '✅'
    }
];

export const PROMPT_BOOSTERS = [
    {
        id: 'cta',
        label: 'Add CTA',
        text: 'Include a concise call to action that invites the viewer to message me for personalized guidance.'
    },
    {
        id: 'brand',
        label: 'Brand polish',
        text: 'Keep the design brand-consistent, premium, and polished enough for a lender marketing campaign.'
    },
    {
        id: 'mobile',
        label: 'Mobile-first',
        text: 'Optimize the composition for mobile readability with short lines, large type, and strong contrast.'
    },
    {
        id: 'compliance',
        label: 'Compliance-safe',
        text: 'Avoid unrealistic promises and keep the overall tone trustworthy, informative, and professional.'
    },
    {
        id: 'lead-gen',
        label: 'Lead generation',
        text: 'Make the layout conversion-focused so it feels compelling in a paid ad or boosted social post.'
    }
];

export const SOCIAL_POST_DRAFT_SEED_KEY = 'social_media_post_draft_seed';

/**
 * Start image generation and return the job
 * @param {Object} options - Generation options
 * @param {string} options.caption - Caption/content for the image
 * @param {string} options.style - Visual style ID
 * @param {string} options.instructions - Design instructions
 * @param {string} options.aspectRatio - Aspect ratio (1:1, 16:9, 9:16)
 * @param {string} [options.brandId] - Optional brand ID
 * @returns {Promise<{ job_id: string, status: string }>}
 */
export async function startImageGeneration(options) {
    const { caption, style, instructions, aspectRatio, brandId } = options;

    const data = {
        caption,
        style,
        instructions,
        aspect_ratio: aspectRatio
    };

    if (brandId) {
        data.brand_id = brandId;
    }

    return generateSocialImage(data);
}

/**
 * Poll a job until complete with configurable intervals and timeout
 * @param {string} jobId - The job ID to poll
 * @param {function} onProgress - Callback for progress updates (progress, message)
 * @returns {Promise<object>} - The completed job result
 */
export async function pollJobUntilComplete(jobId, onProgress) {
    const startTime = Date.now();
    let pollCount = 0;

    return new Promise((resolve, reject) => {
        const poll = async () => {
            try {
                pollCount++;
                const result = await getSocialImageJob(jobId);
                console.log(`[SocialImage] Poll #${pollCount} for ${jobId}:`, result);

                // Handle progress
                const progress = result.progress ?? result.percent ?? result.percentage ?? 0;
                const message = result.message ?? result.status_message ?? `Processing... (${progress}%)`;

                if (onProgress && progress !== undefined) {
                    onProgress(progress, message);
                }

                // Handle status
                const status = (result.status || result.state || '').toUpperCase();

                if (status === 'COMPLETED' || status === 'COMPLETE' || status === 'SUCCESS' || status === 'DONE') {
                    console.log('[SocialImage] Job completed:', result);
                    resolve(result);
                    return;
                }

                if (status === 'FAILED' || status === 'ERROR' || status === 'FAILURE') {
                    const error = new Error(result.error || result.errorMessage || result.error_message || 'Job failed');
                    error.code = 'JOB_FAILED';
                    error.creditsRefunded = result.creditsRefunded || result.credits_refunded || false;
                    reject(error);
                    return;
                }

                // Check timeout
                if (Date.now() - startTime > POLL_TIMEOUT) {
                    const error = new Error('Job timed out');
                    error.code = 'JOB_TIMEOUT';
                    reject(error);
                    return;
                }

                // Continue polling
                setTimeout(poll, POLL_INTERVAL);
            } catch (err) {
                console.error('[SocialImage] Poll error:', err);
                reject(err);
            }
        };

        poll();
    });
}

/**
 * Generate image and poll until complete
 * @param {Object} options - Generation options
 * @param {function} onProgress - Progress callback
 * @returns {Promise<object>} - Final result with image URL
 */
export async function generateAndPoll(options, onProgress) {
    // Start the generation
    const job = await startImageGeneration(options);

    if (!job.job_id) {
        throw new Error('Invalid response: missing job_id');
    }

    // Poll until complete
    return pollJobUntilComplete(job.job_id, onProgress);
}

/**
 * Get the most likely image URL from an API response object
 * @param {Object} image
 * @returns {string}
 */
export function getImageUrl(image) {
    return image?.result_url || image?.url || image?.image_url || '';
}

/**
 * Normalize an image record so the UI can render a consistent shape
 * @param {Object} image
 * @returns {Object}
 */
export function normalizeGeneratedImage(image = {}) {
    return {
        id: image.id || image.image_id || image.job_id || Date.now().toString(),
        result_url: getImageUrl(image),
        caption: image.caption || image.prompt || '',
        style: image.style || 'modern',
        aspect_ratio: image.aspect_ratio || image.aspectRatio || '1:1',
        created_at: image.created_at || image.createdAt || new Date().toISOString(),
        status: image.status || 'completed'
    };
}

/**
 * Fetch user's generated images
 * @param {Object} [params] - Query parameters
 * @returns {Promise<{ images: Array, cursor: string|null, has_more: boolean }>}
 */
export async function fetchImages(params = {}) {
    const response = await getSocialImages(params);
    return {
        ...response,
        images: (response.images || []).map(normalizeGeneratedImage)
    };
}

/**
 * Delete an image
 * @param {string} imageId - Image ID to delete
 * @returns {Promise<{ success: boolean }>}
 */
export async function removeImage(imageId) {
    return deleteSocialImage(imageId);
}

/**
 * Download an image from URL
 * @param {string} url - Image URL
 * @param {string} [filename] - Optional filename
 */
export function downloadImage(url, filename = 'social-image.png') {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Request professional designer service
 * @param {Object} data - Service request data
 * @returns {Promise<{ order_id: string, checkout_url: string }>}
 */
export async function orderDesignerService(data) {
    return requestDesignerService(data);
}

/**
 * Get style by ID
 * @param {string} styleId - Style ID
 * @returns {Object|undefined}
 */
export function getStyleById(styleId) {
    return IMAGE_STYLES.find(s => s.id === styleId);
}

/**
 * Get aspect ratio by ID
 * @param {string} ratioId - Aspect ratio ID
 * @returns {Object|undefined}
 */
export function getAspectRatioById(ratioId) {
    return ASPECT_RATIOS.find(r => r.id === ratioId);
}

/**
 * Build a post composer seed from a generated image
 * @param {Object} image
 * @returns {Object}
 */
export function buildPostComposerSeed(image) {
    const normalizedImage = normalizeGeneratedImage(image);
    return {
        id: `ai-image-${normalizedImage.id}-${Date.now()}`,
        source: 'social-image-generator',
        content: normalizedImage.caption,
        mediaUrls: normalizedImage.result_url
            ? [
                {
                    url: normalizedImage.result_url,
                    type: 'image/png',
                    name: normalizedImage.caption || 'AI generated social image'
                }
            ]
            : [],
        createdAt: new Date().toISOString()
    };
}

/**
 * Save a post composer seed for later consumption
 * @param {Object} seed
 */
export function savePostComposerSeed(seed) {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(SOCIAL_POST_DRAFT_SEED_KEY, JSON.stringify(seed));
}

/**
 * Consume a saved post composer seed
 * @returns {Object|null}
 */
export function consumePostComposerSeed() {
    if (typeof window === 'undefined') return null;
    const rawSeed = window.sessionStorage.getItem(SOCIAL_POST_DRAFT_SEED_KEY);

    if (!rawSeed) {
        return null;
    }

    window.sessionStorage.removeItem(SOCIAL_POST_DRAFT_SEED_KEY);

    try {
        return JSON.parse(rawSeed);
    } catch (error) {
        console.error('Failed to parse saved social post draft seed:', error);
        return null;
    }
}
